<template>
  <div class="digital-lecture-detail-page">
    <div class="container">
      <BaseBreadcrumbs :items="breadcrumbs" />

      <div v-if="video" class="wrapper-grid">
        <div class="main">
          <DigitalLectureDetailVideo
            :video="video"
            :is-liked="isLiked"
            :likes-count="likesCount"
            @like="toggleLike"
            @video-started="onVideoStarted"
          />
          <DigitalLectureDetailSpeakerList
            v-if="video.speakers.length"
            :speakers="video.speakers"
          />
        </div>

        <aside class="side">
          <div class="sticky">
            <DigitalLectureDetailDocumentsList
              v-if="video.materials.length"
              :materials="video.materials"
            />
            <DigitalLectureDetailRecommendationsList
              v-if="isDesktop || isTablet"
              :recommendations="randomVideos"
            />
          </div>
        </aside>
      </div>

      <DigitalLectureDetailVideoSynopsis v-if="video?.description" :content="video.description" />

      <DigitalLectureDetailRecommendationsList
        v-if="(isMobile || isTabletSmall) && randomVideos.length"
        :recommendations="randomVideos"
      />

      <ArticleSlider v-if="video?.news.length" title="Читайте на эту тему" :items="video.news" />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { BaseBreadcrumbs } from '~/components/base-ui/breadcrumbs'
  import {
    DigitalLectureDetailVideo,
    DigitalLectureDetailSpeakerList,
    DigitalLectureDetailDocumentsList,
    DigitalLectureDetailRecommendationsList,
    DigitalLectureDetailVideoSynopsis,
  } from '~/components/digital-lecture/digital-lecture-detail'
  import { ArticleSlider } from '~/components/article/slider'
  import { useDigitalLectureDetail } from '~/composables/digital-lecture/use-digital-lecture-detail'
  import { useDigitalLectureRandom } from '~/composables/digital-lecture/use-digital-lecture-random'
  import { useAuth } from '~/composables/use-auth'
  import { useAuthModal } from '~/composables/use-auth-modal'
  import { useNotificationsStore } from '~/store/notifications'
  import {
    hasDigitalLectureSkipAuth,
    setDigitalLectureSkipAuth,
  } from '~/shared/utils/pages/digital-lecture'
  import { useMpsPhone } from '~/composables/digital-lecture/use-mps-phone'

  const route = useRoute()
  const slug = String(route.params.slug ?? '')

  if (!slug) {
    throw createError({ statusCode: 404, message: 'Страница не найдена' })
  }

  const {
    detail: video,
    error,
    isLiked,
    likesCount,
    toggleLike,
  } = await useDigitalLectureDetail(slug)

  if (error.value || !video.value?.id) {
    throw createError({ statusCode: 404, message: 'Страница не найдена' })
  }

  const { isDesktop, isTablet, isMobile, isTabletSmall } = useBreakpoint()

  const excludeIds = computed(() => (video.value?.id ? [video.value.id] : []))
  const { items: randomVideos } = useDigitalLectureRandom(excludeIds)

  const { breadcrumbs } = await useBreadcrumbs()

  const { $services } = useNuxtApp()
  const { isAuth } = useAuth()
  const { openModal } = useAuthModal()
  const notifications = useNotificationsStore()

  const completedLectureSlug = ref<string | null>(null)

  const { getMpsPhone } = useMpsPhone()

  async function onVideoStarted() {
    if (!slug || completedLectureSlug.value === slug) {
      return
    }

    const phone = getMpsPhone()

    if (!isAuth.value) {
      if (hasDigitalLectureSkipAuth(slug)) {
        return
      }

      if (!phone) {
        void openModal({
          backUrl: route.fullPath,
          title: 'Хотите добавить лекцию в просмотренные?\nВойдите в личный кабинет.',
          showContinueWithoutAuth: true,
          onContinueWithoutAuth: () => setDigitalLectureSkipAuth(slug),
        })
        return
      }

      // Пользователь не авторизован, но известен телефон (MPS): фиксируем просмотр за ним
      completedLectureSlug.value = slug

      const isSuccess = await $services.digitalLectureService.detail.addView(slug, phone)
      if (!isSuccess) {
        completedLectureSlug.value = null
      }
      return
    }

    completedLectureSlug.value = slug

    const isSuccess = await $services.digitalLectureService.detail.addView(slug, phone)
    if (isSuccess) {
      notifications.pushSuccess('Лекция добавлена в просмотренные.')
    } else {
      completedLectureSlug.value = null
    }
  }

  useSeoMeta({
    title: video.value?.name,
    description: video.value?.short_description || undefined,
    ogTitle: video.value?.name,
    ogDescription: video.value?.short_description || undefined,
    ogImage: video.value?.preview || undefined,
  })
</script>

<style lang="scss" scoped>
  .digital-lecture-detail-page {
    display: flex;
    justify-content: center;
    padding: 40px 0;

    > .container {
      @extend %flex-column;

      gap: 56px;
      max-width: 1440px;
      width: 100%;
      padding: 0 80px;

      @include vp-1279 {
        padding: 0 40px;
        gap: 24px;
      }

      @include vp-767 {
        padding: 0 16px;
      }
    }

    > .container > .wrapper-grid {
      display: grid;
      grid-template-columns: 1fr 408px;
      gap: 32px;
      margin-top: -32px;

      @include vp-1279 {
        grid-template-columns: 1fr;
        gap: 24px;
        margin-top: 0;
      }

      @include vp-767 {
        display: block;
      }
    }

    > .container > .wrapper-grid > .main {
      @extend %flex-column;

      gap: 24px;
      min-width: 0;
    }

    > .container > .wrapper-grid > .side {
      min-width: 0;

      > .sticky {
        position: sticky;
        top: 100px;
        @extend %flex-column;

        gap: 24px;
      }
    }
  }
</style>
